import { CURATOR_OFFER_GENRE_SET, MAX_CURATOR_OFFER_GENRES } from '../constants/curatorOfferGenres';

export interface CuratorOfferSubmissionPlaylist {
  playlistId: string;
  name?: string;
  imageUrl?: string;
  externalUrl?: string;
}

export interface CuratorOffer {
  dailyStreamsMin: number;
  dailyStreamsMax: number;
  defaultRatePer1k: number;
  notes: string;
  genres: string[];
  interestedInFeedback: boolean;
  audienceCountryCodes: string[];
  audienceCountriesUnknown: boolean;
  submissionPlaylists: CuratorOfferSubmissionPlaylist[];
  submissionPlaylistIds?: string[];
  /**
   * When true, curator opted into "all owned playlists" mode.
   * New owned playlists should be included automatically until they cherry-pick.
   */
  includeAllOwnedPlaylists?: boolean;
  offerTemplateId?: string | null;
  updatedAt?: Date | string | null;
  // Legacy optional on read
  streamsMin?: number | null;
  streamsMax?: number | null;
  turnaroundDays?: number | null;
}

/** True when selected IDs cover every currently owned playlist. */
export const selectionCoversAllOwned = (
  selectedIds: string[],
  ownedIds: string[]
): boolean => {
  if (!ownedIds.length) return false;
  const selected = new Set(selectedIds.filter(Boolean));
  if (selected.size !== ownedIds.length) return false;
  return ownedIds.every(id => selected.has(id));
};

const toPositiveNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const isNewShapeComplete = (offer: Partial<CuratorOffer> | null | undefined): boolean => {
  if (!offer) return false;

  const dailyStreamsMin = toPositiveNumber(offer.dailyStreamsMin);
  const dailyStreamsMax = toPositiveNumber(offer.dailyStreamsMax);
  if (!dailyStreamsMin || !dailyStreamsMax || dailyStreamsMax < dailyStreamsMin) return false;

  if (!toPositiveNumber(offer.defaultRatePer1k)) return false;

  const genres = Array.isArray(offer.genres) ? offer.genres : [];
  if (genres.length === 0 || genres.length > MAX_CURATOR_OFFER_GENRES) return false;
  if (genres.some(genre => !CURATOR_OFFER_GENRE_SET.has(String(genre)))) return false;

  if (typeof offer.interestedInFeedback !== 'boolean') return false;

  if (offer.audienceCountriesUnknown === true) {
    // ok
  } else {
    const codes = Array.isArray(offer.audienceCountryCodes) ? offer.audienceCountryCodes : [];
    if (codes.length < 1 || codes.length > 5) return false;
  }

  // "All owned" mode is a complete playlist preference even if snapshot is stale
  if (offer.includeAllOwnedPlaylists === true) {
    return true;
  }

  const playlistCount =
    (Array.isArray(offer.submissionPlaylists) ? offer.submissionPlaylists.length : 0) ||
    (Array.isArray(offer.submissionPlaylistIds) ? offer.submissionPlaylistIds.length : 0);
  if (playlistCount < 1) return false;

  return true;
};

/**
 * True only when a real Spotify curator connection exists.
 * Do NOT treat Auth0 userInfo.token as Spotify (that was a false positive).
 */
export const isSpotifyConnectedForCurator = (): boolean => {
  try {
    const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
    // Real Spotify profile markers (own account or shared profile with owner)
    if (spotifyUser?.username || spotifyUser?.spotify_user_id || spotifyUser?.id) {
      return true;
    }
    // Shared profiles may store owner without username immediately
    if (spotifyUser?.isShared === true && spotifyUser?.ownerId) {
      return true;
    }
    // Explicit connection flag only if paired with a real Spotify access token
    if (localStorage.getItem('hasSpotifyConnection') === 'true') {
      const hasSpotifyToken = Boolean(localStorage.getItem('SPOTIFY_TOKEN'));
      if (hasSpotifyToken) return true;
      return false;
    }
    return false;
  } catch {
    return false;
  }
};
